import { useMemo, useState } from "react";
import useIsCreate from "hooks/useIsCreate";
import CircularLoading from "components/CircularLoading";
import {
  Competency_Map_Status,
  PlatformCompetencyMapListOrderFields,
  OrderType,
  useCompetencyMapOptionsQuery,
  useTrialInfoQuery,
  Trial_Status,
} from "@generated/graphql";
import Box from "@mui/material/Box";
import SaveEditButtons from "components/buttons/SaveEditButtons";
import { useForm } from "react-hook-form";
import { Grid } from "@mui/material";
import Form from "components/form/Form";
import { useParams, useSearchParams } from "react-router-dom";
import qs from "qs";
import { TRIAL_STATUS_TITLE } from "constants/global";
import useInitTrialForm from "./hooks/useInitTrialForm";
import useUpdateCandidate from "./hooks/useUpdateCandidate";
import { InfoInputs } from "./types";
import InfoBlock from "../../../components/InfoBlock";
import InfoTabLeft from "./components/Left";
import InfoTabRight from "./components/Right";
import TrialHistory from "./components/TrialHistory";

const InfoTab = () => {
  const { id } = useParams();
  const isCreate = useIsCreate();
  const [isEditing, setIsEditing] = useState<boolean>(isCreate);

  const { data, loading } = useTrialInfoQuery({
    variables: {
      trialId: id!,
    },
    skip: !id,
  });

  const { data: competencyMapsData, loading: competencyMapsLoading } = useCompetencyMapOptionsQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      order: {
        field: PlatformCompetencyMapListOrderFields.CreatedAt,
        order: OrderType.Desc,
      },
      filter: {
        status: Competency_Map_Status.Approved,
      },
    },
  });

  const trial = data?.trial;
  const competencyMaps = competencyMapsData?.competencyMaps.data;

  const [searchParams] = useSearchParams();
  const params = useMemo(() => qs.parse(searchParams.toString()), [searchParams]);
  const { competencyMapId } = params as unknown as { competencyMapId?: string };
  const competencyMap =
    isCreate && competencyMapId
      ? competencyMaps?.find((map) => map.id === competencyMapId)
      : trial?.examination.competencyMap;

  const initFormData = useInitTrialForm(trial, competencyMap);

  const form = useForm<InfoInputs>({
    values: initFormData,
  });
  const { handleSubmit } = form;

  const { onUpdateCompanyCandidate, updateCompanyCandidateLoading, updateTrialLoading, createExaminationLoading } =
    useUpdateCandidate({
      setIsEditing,
    });

  const isEditingButtonDisable = trial?.status !== Trial_Status.Created;

  if (loading || competencyMapsLoading) return <CircularLoading />;

  return (
    <Box sx={{ py: 2 }}>
      <Form form={form}>
        <Grid
          container
          rowSpacing={2.5}>
          <InfoBlock
            item
            LeftElement={
              <InfoTabLeft
                isEditing={isEditing}
                form={form}
              />
            }
            RightElement={
              <InfoTabRight
                competencyMaps={competencyMaps}
                competencyMapsLoading={competencyMapsLoading}
                isEditing={isEditing}
                form={form}
              />
            }
            ControlsElement={
              <SaveEditButtons
                isEdited={isEditing}
                onSubmit={handleSubmit(onUpdateCompanyCandidate)}
                setIsEdited={setIsEditing}
                editingButtonTooltipText={
                  isEditingButtonDisable && trial
                    ? `Редактирование возможно в статусе "${TRIAL_STATUS_TITLE[trial.status]}"`
                    : undefined
                }
                editingButtonDisable={isEditingButtonDisable}
                loading={updateTrialLoading || createExaminationLoading || updateCompanyCandidateLoading}
              />
            }
          />
          <Grid
            item
            xs={12}>
            <TrialHistory trial={trial} />
          </Grid>
        </Grid>
      </Form>
    </Box>
  );
};

export default InfoTab;
